import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, ReceiptX, FolderPlus } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StatusPill } from "../components/StatusPill";
import { ExpensePanel } from "../components/ExpensePanel";
import { CompanySwitcher } from "../components/CompanySwitcher";
import { api, errMsg, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";

const CATEGORIES = ["Travel", "Meals", "Lodging", "Transport", "Office Supplies", "Software", "Equipment", "Entertainment", "Utilities", "Other"];
const STATUSES = ["draft", "submitted", "approved", "rejected", "reimbursed"];

const Metric = ({ label, value, testId }) => (
  <div className="px-5 py-4 border-r border-slate-200 last:border-r-0 min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</p>
    <p data-testid={testId} className="font-mono text-xl font-semibold text-slate-900 mt-1 truncate">{value}</p>
  </div>
);

export default function DashboardPage({ workspace }) {
  const { user } = useAuth();
  const { t } = useT();
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState({});
  const [panel, setPanel] = useState(null); // null | {} (new) | expense
  const [filters, setFilters] = useState({ status: "all", category: "all", search: "" });
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState([]);
  const [reportDialog, setReportDialog] = useState(false);
  const [reportTitle, setReportTitle] = useState("");
  const isFreelancer = workspace?.type === "freelancer";
  const cur = workspace?.currency || "USD";

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const createReport = async () => {
    try {
      const { data } = await api.post("/reports", { title: reportTitle, expense_ids: selected });
      toast.success(data.status === "approved" ? t("toast_reportAutoApproved") : t("toast_reportSubmitted"));
      setSelected([]);
      setReportDialog(false);
      setReportTitle("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const load = useCallback(async () => {
    const params = {};
    if (filters.status !== "all") params.status = filters.status;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.search) params.search = filters.search;
    const [ex, st] = await Promise.all([api.get("/expenses", { params }), api.get("/stats")]);
    setExpenses(ex.data);
    setStats(st.data);
    setLoaded(true);
  }, [filters]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{t("expenses_title")}</h1>
          <p className="text-sm text-slate-500">{isFreelancer ? t("dash_sub_fl") : t("dash_sub_team")}</p>
        </div>
        <Button data-testid="new-expense-btn" onClick={() => setPanel({})} className="rounded-none bg-slate-900 hover:bg-slate-700">
          <Plus size={16} className="mr-1.5" /> {t("newExpense")}
        </Button>
      </div>

      <CompanySwitcher />

      {isFreelancer ? (
        <div className="border border-slate-200 grid grid-cols-2">
          <Metric label={t("thisMonth")} value={fmtMoney(stats.month_total, cur)} testId="stat-month-total" />
          <Metric label={t("entries")} value={stats.total_count ?? 0} testId="stat-pending" />
        </div>
      ) : (
        <div className="border border-slate-200 grid grid-cols-2 lg:grid-cols-4">
          <Metric label={t("thisMonth")} value={fmtMoney(stats.month_total, cur)} testId="stat-month-total" />
          <Metric label={t("pendingApproval")} value={stats.pending_count ?? 0} testId="stat-pending" />
          <Metric label={t("pendingAmount")} value={fmtMoney(stats.pending_amount, cur)} testId="stat-pending-amount" />
          <Metric label={t("approvedReimbursed")} value={fmtMoney(stats.approved_amount, cur)} testId="stat-approved" />
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-input" placeholder={t("searchVendor")} value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="rounded-none pl-9 h-9" />
        </div>
        {!isFreelancer && (
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="status-filter" className="rounded-none w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{t(`st_${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}>
          <SelectTrigger data-testid="category-filter" className="rounded-none w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allCategories")}</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selected.length > 0 && (
        <div className="border border-slate-900 bg-slate-900 text-white px-4 py-2 flex items-center justify-between" data-testid="selection-bar">
          <span className="text-sm font-mono">{selected.length} {t("selected")}</span>
          <div className="flex items-center gap-3">
            <button data-testid="clear-selection-btn" onClick={() => setSelected([])} className="text-xs text-slate-300 hover:text-white transition-colors duration-150">{t("clearSel")}</button>
            <Button data-testid="create-report-btn" onClick={() => setReportDialog(true)} className="rounded-none bg-white text-slate-900 hover:bg-slate-200 h-8 text-xs">
              <FolderPlus size={14} className="mr-1.5" /> {t("groupIntoReport")}
            </Button>
          </div>
        </div>
      )}

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="w-8 px-3" />
              {[t("th_date"), t("th_vendor"), t("th_category"), !isFreelancer && t("th_submittedBy"), isFreelancer && t("th_client"), t("th_amount"), !isFreelancer && t("th_status")].filter(Boolean).map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="expenses-table-body">
            {expenses.map((e) => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} onClick={() => setPanel(e)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-150">
                <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                  {e.user_id === user.id && ["draft", "rejected"].includes(e.status) && (
                    <input type="checkbox" data-testid={`select-expense-${e.id}`} checked={selected.includes(e.id)} onChange={() => toggleSelect(e.id)} className="accent-slate-900 cursor-pointer" />
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-500 whitespace-nowrap">{e.date || "—"}</td>
                <td className="px-3 py-2.5 font-medium text-slate-900">{e.vendor || t("untitled")}</td>
                <td className="px-3 py-2.5 text-slate-500">{t(`cat_${e.category}`)}</td>
                {!isFreelancer && <td className="px-3 py-2.5 text-slate-500">{e.user_name}</td>}
                {isFreelancer && <td className="px-3 py-2.5 text-slate-500">{e.client || "—"}</td>}
                <td className="px-3 py-2.5 font-mono font-semibold text-slate-900 whitespace-nowrap">{fmtMoney(e.amount, e.currency)}</td>
                {!isFreelancer && <td className="px-3 py-2.5"><StatusPill status={e.status} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {loaded && expenses.length === 0 && (
          <div className="py-20 flex flex-col items-center gap-4" data-testid="empty-state">
            <p className="font-mono text-6xl font-light text-slate-200">0</p>
            <div className="text-center">
              <p className="font-heading font-semibold text-slate-900">{t("noExpenses")}</p>
              <p className="text-sm text-slate-500 mt-1">{t("emptyHint")}</p>
            </div>
            <Button data-testid="empty-new-expense-btn" onClick={() => setPanel({})} className="rounded-none bg-slate-900 hover:bg-slate-700 mt-2">
              <Plus size={16} className="mr-1.5" /> {t("addFirst")}
            </Button>
          </div>
        )}
        {!loaded && <div className="py-16 flex justify-center"><ReceiptX size={24} className="text-slate-200 animate-pulse" /></div>}
      </div>

      {panel !== null && (
        <ExpensePanel expense={panel.id ? panel : null} workspace={workspace} onClose={() => setPanel(null)} onChanged={load} />
      )}

      <Dialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("reportDialogTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">{t("reportDialogDesc1")} {selected.length} {t("reportDialogDesc2")}</p>
          <Input data-testid="report-title-input" placeholder={t("reportTitlePh")} value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} className="rounded-none h-11" />
          <Button data-testid="report-submit-btn" onClick={createReport} disabled={!reportTitle.trim()} className="rounded-none w-full bg-slate-900 hover:bg-slate-700">{t("submitReport")}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
